import React, { useState, useEffect } from 'react';

import { useNavigate } from 'react-router-dom';

import styled from 'styled-components';

import { useAuth } from 'contexts/AuthContext';

import api from 'services/api';

import { theme } from 'styles/theme';

import { DEFAULT_LOGIN_TAGLINE } from 'config/tenantBranding';



const LoginPage = styled.div`

  display: flex;

  min-height: 100vh;

  background: ${theme.colors.background.page};

`;



const LoginHero = styled.div`

  flex: 1;

  display: flex;

  flex-direction: column;

  justify-content: center;

  padding: ${theme.spacing.xxl};

  background: linear-gradient(
    135deg,
    ${theme.colors.primaryLight} 0%,
    ${theme.colors.quaternary} 50%,
    ${theme.colors.white} 100%
  );

  position: relative;

  overflow: hidden;



  &::before {

    content: '';

    position: absolute;

    width: 400px;

    height: 400px;

    border-radius: 50%;

    background: rgba(37, 99, 235, 0.08);

    top: -100px;

    right: -100px;

  }

`;



const HeroTitle = styled.h1`

  font-size: 32px;

  font-weight: 700;

  color: ${theme.colors.text.primary};

  margin-bottom: ${theme.spacing.sm};

  position: relative;

  letter-spacing: -0.02em;

`;



const LoginPanel = styled.div`

  width: 440px;

  max-width: 100%;

  display: flex;

  align-items: center;

  justify-content: center;

  padding: ${theme.spacing.xl};

  background: ${theme.colors.white};

  border-left: 1px solid ${theme.colors.border};

  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.04);

`;



const LoginCard = styled.div`

  width: 100%;

  max-width: 360px;

`;



const LoginTitle = styled.h2`

  font-size: ${theme.typography.fontSize.xxl};

  font-weight: 700;

  color: ${theme.colors.text.primary};

  margin-bottom: ${theme.spacing.xxs};

`;



const LoginDesc = styled.p`

  font-size: ${theme.typography.fontSize.sm};

  color: ${theme.colors.text.tertiary};

  margin-bottom: ${theme.spacing.lg};

`;



const LoginForm = styled.form`

  display: flex;

  flex-direction: column;

  gap: ${theme.spacing.md};

`;



const FormGroup = styled.div`

  display: flex;

  flex-direction: column;

  gap: ${theme.spacing.xxs};

`;



const Label = styled.label`

  font-size: ${theme.typography.fontSize.sm};

  font-weight: 500;

  color: ${theme.colors.text.secondary};

`;



const Input = styled.input`

  padding: 10px 12px;

  border: none;

  border-bottom: 1px solid ${theme.colors.border};

  border-radius: 0;

  font-size: ${theme.typography.fontSize.md};

  background: transparent;

  transition: border-color 0.2s;



  &:focus {

    outline: none;

    border-bottom-color: ${theme.colors.primary};

  }

`;



const Button = styled.button`

  margin-top: ${theme.spacing.sm};

  padding: 12px ${theme.spacing.lg};

  background-color: ${theme.colors.navy};

  color: ${theme.colors.white};

  border: none;

  border-radius: ${theme.borderRadius.md};

  font-size: ${theme.typography.fontSize.md};

  font-weight: 600;

  cursor: pointer;

  transition: opacity 0.2s;



  &:hover:not(:disabled) {

    opacity: 0.9;

  }



  &:disabled {

    opacity: 0.5;

    cursor: not-allowed;

  }

`;



const ErrorMessage = styled.p`

  color: ${theme.colors.status.error};

  font-size: ${theme.typography.fontSize.sm};

  margin: 0;

`;



const Hint = styled.p`

  color: ${theme.colors.text.tertiary};

  font-size: ${theme.typography.fontSize.xs};

  margin: 0;

`;



const LAST_COMP_CD_KEY = 'erp.lastCompCd';

const Login = () => {

  const [compCd, setCompCd] = useState(() => {
    try {
      return localStorage.getItem(LAST_COMP_CD_KEY) || '';
    } catch {
      return '';
    }
  });

  const [username, setUsername] = useState('admin');

  const [password, setPassword] = useState('admin');

  const [error, setError] = useState('');

  const [loading, setLoading] = useState(false);

  const { login, isAuthenticated, loading: authLoading } = useAuth();

  const navigate = useNavigate();



  useEffect(() => {

    if (!authLoading && isAuthenticated) {

      navigate('/', { replace: true });

    }

  }, [isAuthenticated, authLoading, navigate]);



  const handleSubmit = async (e) => {

    e.preventDefault();

    setError('');

    setLoading(true);

    try {

      const response = await api.post('/auth/login', {

        compCd: compCd.trim(),

        username: username.trim(),

        password,

      });

      const data = response.data;

      if (data.success && data.token) {

        try {
          localStorage.setItem(LAST_COMP_CD_KEY, compCd.trim());
        } catch {
          /* ignore */
        }
        login(data.token);

        navigate('/', { replace: true });

      } else {

        setError(data.message || '로그인에 실패했습니다.');

      }

    } catch (err) {

      setError(err.data?.message || err.message || '로그인에 실패했습니다.');

    } finally {

      setLoading(false);

    }

  };



  return (

    <LoginPage>

      <LoginHero>

        <HeroTitle>{DEFAULT_LOGIN_TAGLINE}</HeroTitle>

      </LoginHero>

      <LoginPanel>

        <LoginCard>

          <LoginTitle>로그인</LoginTitle>

          <LoginDesc>기관코드와 계정 정보를 입력하세요</LoginDesc>

          <LoginForm onSubmit={handleSubmit}>

            <FormGroup>

              <Label htmlFor="compCd">기관코드</Label>

              <Input id="compCd" value={compCd} onChange={(e) => setCompCd(e.target.value)} required />

              <Hint>예: DEMO-LFO(데모) · GNK(운영테스트)</Hint>

            </FormGroup>

            <FormGroup>

              <Label htmlFor="username">아이디</Label>

              <Input id="username" value={username} onChange={(e) => setUsername(e.target.value)} required />

            </FormGroup>

            <FormGroup>

              <Label htmlFor="password">비밀번호</Label>

              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />

              <Hint>데모: admin / admin</Hint>

            </FormGroup>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <Button type="submit" disabled={loading}>{loading ? '로그인 중...' : '로그인'}</Button>

          </LoginForm>

        </LoginCard>

      </LoginPanel>

    </LoginPage>

  );

};



export default Login;

